import { request, requestJSON } from '@codametrix/client-api';
import {
  appServiceLines,
  caseUtils,
  cmxDateTime,
  codeBenchActions,
  HttpMethods
} from '@codametrix/ui-common';
import type { CodingScreenApi, HoldPayload } from '../../project_starburst';
import { holdCaseArgsFromPayload } from './apiMappers/holdCaseArgs';
import { toProviderDirectoryEntry } from './apiMappers/providerDirectory';

export type DataAccessContext = {
  readonly sourceId: string;
  readonly serviceLine: string;
  readonly healthSystemUid: string;
  readonly tenantUid: string;
};

import { gql } from '@apollo/client';
import type {
  TransferRecipient
} from '../../project_starburst';
import type {
  BillingLocationUpdate,
  DictionaryOption,
  DictionaryRequest
} from '../../project_starburst/interface/api';
import { api as netApi } from '../../core/net';
import {
  narrowByTerm,
  toTransferRecipients
} from './apiMappers/transferRecipients';

/**
 * the coder directory behind the Transfer modal's type-ahead.
 *
 * A DASHBOARD-OWNED document deliberately: the equivalent query lives in `packages/service-desk`,
 * which dashboard-ui does not depend on and `project_starburst` may not import. Only the transport
 * (`getServiceDeskClient`) is shared, exactly as `actions/manage-coders.action.ts` does it.
 */
const GET_ALL_CODERS = gql`
  query Coders {
    Coders {
      data
      total
    }
  }
`;

/**
 * Reconstruct the hold endpoint's `ProcessInstance{serviceLine, guid.uuid}` from the fetched coding
 * case. `state.codeBench.processInstance` is NOT reliably populated on the `/project_starburst/`
 * route (only amplify case-load actions write it), so read the authoritative values off the primary
 * `CaseRun` — the same `process_instance_guid` + resolved `amplifyPath` the app's own amplify fetch
 * uses (`getDetail` → `amplifyProcessInstance`). Returns undefined until the case has loaded, or if
 * the service line can't be resolved — and NEVER throws (a throw here would crash the shell render).
 */
export function processInstanceFromCase(
  activeCase: CMxAPI.Case | undefined
): AmplifyAPI.ProcessInstance | undefined {
  if (!activeCase?.caseRuns?.length) return undefined;
  try {
    const caseRun = caseUtils.getCaseRun(activeCase);
    const uuid = caseRun?.process_instance_guid;
    if (!uuid) return undefined;
    const slConfig = appServiceLines.determineServiceLineConfig({
      name: caseRun.service_line,
      caseIdentifier: String(caseRun.case_id ?? '')
    });
    if (!slConfig?.amplifyPath) return undefined;
    return {
      serviceLine: slConfig.amplifyPath,
      guid: { uuid }
    } as AmplifyAPI.ProcessInstance;
  } catch {
    return undefined;
  }
}

const IPC_LONG_NAME = 'Inpatient Professional Coding';

const amplifyServiceLineAlias = (serviceLine: string): string =>
  serviceLine === IPC_LONG_NAME ? 'ipc' : serviceLine;

/** The `dataaccess` branch of `navigateProvider` (`ui-common`'s `code-bench.actions.ts`). */
async function fetchProviderViaDataAccess(
  dataAccessContext: DataAccessContext,
  providerName: string | undefined,
  tenantCode: string | undefined
): Promise<XCaliberAPI.ProviderEntityIPC[] | undefined> {
  const body: CMxCommonApp.ProviderSearchRequest = {
    ...dataAccessContext,
    name: 'searchForProvidersByName',
    action: 'read',
    actionDestination: 'searchbillingprovider',
    sort: 'Asc',
    limit: 1000,
    filterFieldsMap: {
      provider_name: providerName,
      ...(tenantCode ? { location_name: tenantCode } : {})
    }
  };
  const result = await requestJSON<CMxAPI.PageableList<XCaliberAPI.ProviderEntityIPC>>(
    '/dataaccess/read/v1',
    { method: 'POST', body: JSON.stringify(body) }
  );
  return result?.content;
}

/**
 * Build the `CodingScreenApi` the coding shell injects. ONLY `placeHold` is wired to the real backend
 * (direct `PUT /coding-api/.../hold/v1` via client-api's `request`, which throws on non-2xx so a failed
 * hold actually surfaces); the rest are resolving stubs: `loadNextCase` needs the worklist cursor
 * context this route lacks, and the "Logged as" current-user has no redux source yet — both PT-1 on CDMX-18412.
 *
 * Returns undefined until the case + its ProcessInstance are available, during which `HoldAction`
 * shows "Holds are not available yet." (its own no-api guard).
 */
export function codingScreenApiFromCase(
  activeCase: CMxAPI.Case | undefined,
  dispatch: (action: unknown) => unknown,
  dataAccessContext: DataAccessContext,
  reloadCase: () => void,
  tenantId: string
): CodingScreenApi | undefined {
  const processInstance = processInstanceFromCase(activeCase);
  if (!processInstance) return undefined;
  return {
    placeHold: async (payload: HoldPayload): Promise<void> => {
      // Issue the hold PUT DIRECTLY via client-api's `request`, which THROWS on any non-2xx
      // (ServerError/BadRequestError/UnauthenticatedError/…) -> this promise rejects -> HoldAction's
      // placeHold `catch` shows the error banner. We deliberately do NOT go through ui-common's
      // `holdCase` thunk: it catches the request error, fires a legacy toast, and still resolves
      // `fulfilled`, so its `.unwrap()` never rejects and a FAILED hold would present as success.
      const args = holdCaseArgsFromPayload(payload, processInstance);
      await request(
        `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/hold/v1`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          body: JSON.stringify({
            hold_reason: args.holdReason,
            comment: args.comment,
            coding_manager_id: args.coding_manager_id ?? null
          })
        }
      );
      // Refresh the case's hold status into redux (the side effect the `holdCase` thunk used to
      // provide; PT-1 will consume it to reflect the now-on-hold state). Fire-and-forget: the hold
      // already persisted above, so a status-refresh failure must never fail the placeHold.
      dispatch(codeBenchActions.getHoldCaseForProcessInstance(processInstance));
    },
    updateBillingLocation: async ({
      caseRunUid,
      serviceLine,
      billingLocation
    }: BillingLocationUpdate): Promise<void> => {
      // `request` resolves for 405/410/422/428 instead of throwing, so a rejected
      // edit would otherwise read as a successful save.
      const response = await request(
        `/coding-api/processinstance/${amplifyServiceLineAlias(serviceLine)}/${caseRunUid}/v1`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          body: JSON.stringify({ billing_location: billingLocation })
        }
      );
      if (!response.ok) {
        throw new Error(`Facility update failed with ${response.status}`);
      }

      // Past this line the change is committed; nothing below may reject, and
      // neither side effect may cost the other.
      try {
        const updated = await response.json();
        dispatch(codeBenchActions.fetchActivity(updated) as never);
      } catch (error) {
        console.error('Facility saved, but the activity dispatch failed', error);
      }
      try {
        reloadCase();
      } catch (error) {
        console.error('Facility saved, but the refresh failed', error);
      }
    },
    loadDictionaryOptions: async ({
      dictionaryName,
      columnName
    }: DictionaryRequest): Promise<readonly DictionaryOption[]> => {
      const searchParams = new URLSearchParams({
        tenantId,
        derivedIndicator: 'true',
        effectiveDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
        dictionaryName,
        columnName
      });

      const res = await netApi<CMxAPI.RowData>({
        endpoint: `/dictionary/row/unrestricted/v2?${searchParams.toString()}`,
        init: { method: HttpMethods.GET }
      });

      // Only real string cells survive. A null/absent one would `String()` into a selectable --
      // and PERSISTABLE -- "null" billing location, and a repeat collides as a React key downstream.
      const values = (res.rowValues ?? [])
        .map((row: Record<string, unknown>) => row[columnName])
        .filter(
          (value): value is string =>
            typeof value === 'string' && value.length > 0
        );
      return [...new Set(values)].map((value) => ({ label: value, value }));
    },
    loadNextCase: async (): Promise<void> => { },
    loadCase: async (): Promise<void> => { },
    startReview: (): void => { },
    onCodeSearch: async (): Promise<void> => { },
    fetchProviderDetails: async ({ providerNumber, providerName, tenantCode }) => {
      if (!providerNumber) return undefined; // mirrors navigateProvider*'s own guard
      const match = await fetchProviderViaDataAccess(
        dataAccessContext,
        providerName,
        tenantCode
      );
      const found = match?.find((p) => p?.provider_id === providerNumber);
      return found ? toProviderDirectoryEntry(found) : undefined;
    },
    /**
     * the READ half of Transfer, wired for real: it needs no new backend (the coder
     * directory already exists) and it is what makes the type-ahead demonstrable end to end.
     *
     * The WRITE half (`transferCase`) is deliberately NOT provided. No backend can persist the
     * transfer reasons, note or case context, and no transfer audit record exists (ticket
     * AC11/AC12) — so the capability stays absent and `TransferModal` renders its preview notice
     * with a disabled primary action rather than moving a case while silently discarding what the
     * coder typed. Supplying a `transferCase` here is the one-line change that turns the feature on
     * once a real endpoint exists.
     *
     * Never throws: a directory failure degrades the type-ahead to "No matches", it must not take
     * down the modal.
     */
    searchTransferRecipients: async (
      term: string
    ): Promise<readonly TransferRecipient[]> => {
      try {
        // Imported LAZILY, inside the call. `clients/service-desk.client` pulls in
        // `actions/feedback.actions` at module scope, which destructures `commonActions` from
        // `@codametrix/ui-common` -- under this app's existing partial ui-common test mocks that
        // destructure throws at import time and takes down whole suites. A dynamic import keeps the
        // dependency where it is actually used and off every consumer's module-init path.
        const { getServiceDeskClient } = await import(
          '../../clients/service-desk.client'
        );
        const client = getServiceDeskClient();
        await client.refreshToken();
        const response = await client.apolloClient.query({
          query: GET_ALL_CODERS,
          fetchPolicy: 'network-only'
        });
        // `Coders` returns either `{data: {data: rows[], total}}` or `{data: rows[]}`.
        const payload = response?.data?.Coders?.data;
        const rows = Array.isArray(payload) ? payload : (payload?.data ?? []);
        return narrowByTerm(toTransferRecipients(rows), term);
      } catch {
        return [];
      }
    }
  };
}
